export default {
    state: {
        pending: null,                  // array of {enrollment_id, user, course, category}
        currentBatch: null,
        cycleMonth: null,
        canSubmit: true,                // false after the monthly deadline
        submissionDeadlineDay: 15,
        history: null,
        historyNext: undefined,
        formConfigs: {},                // keyed by enrollment_id
    },
    mutations: {
        SET_PENDING(state, { data, meta }) {
            state.pending = data;
            state.currentBatch = meta ? meta.current_batch : null;
            state.cycleMonth = meta ? meta.cycle_month : null;
            state.canSubmit = meta && typeof meta.can_submit === 'boolean' ? meta.can_submit : true;
            state.submissionDeadlineDay = meta && meta.submission_deadline_day ? meta.submission_deadline_day : 15;
        },
        REMOVE_PENDING(state, enrollmentId) {
            if (!state.pending) return;
            // Only drop the "new pending" row for this enrollment; leave any reversed row in place.
            state.pending = state.pending.filter(item => !(item.enrollment_id === enrollmentId && !item.reversed_report));
        },
        REMOVE_REVERSED_PENDING(state, reportId) {
            if (!state.pending) return;
            state.pending = state.pending.filter(item => !(item.reversed_report && item.reversed_report.id === reportId));
        },
        SET_FORM_CONFIG(state, { enrollmentId, config }) {
            state.formConfigs = { ...state.formConfigs, [enrollmentId]: config };
        },
        SET_HISTORY(state, payload) {
            state.history = payload.data;
            state.historyNext = payload.next;
        },
        APPEND_HISTORY(state, payload) {
            state.history = [...(state.history || []), ...payload.data];
            state.historyNext = payload.next;
        },
        ADD_TO_HISTORY(state, report) {
            state.history = [report, ...(state.history || [])];
        },
    },
    actions: {
        loadQuarterlyPending({ commit }) {
            return window.axios.get('/teacher/quarterly-reports/pending').then(response => {
                commit('SET_PENDING', { data: response.data.data, meta: response.data.meta });
                return response;
            });
        },
        loadQuarterlyFormConfig({ state, commit }, enrollmentId) {
            if (state.formConfigs[enrollmentId]) {
                return Promise.resolve({ data: state.formConfigs[enrollmentId] });
            }
            return window.axios.get(`/teacher/quarterly-reports/form-config/${enrollmentId}`).then(response => {
                commit('SET_FORM_CONFIG', { enrollmentId, config: response.data });
                return response;
            });
        },
        loadQuarterlyLessons(_ctx, { enrollmentId }) {
            // Last 3 months of lesson reports for the enrollment — shown for review.
            return window.axios.get(`/teacher/quarterly-reports/lessons/${enrollmentId}`)
                .then(response => (response.data && response.data.lessons) || []);
        },
        generateQuarterlyDraft(_ctx, { enrollmentId }) {
            // AI-drafts the free-text fields from the enrollment's recent lessons.
            // Does not persist anything — the teacher reviews, edits, then submits.
            return window.axios.post(`/teacher/quarterly-reports/generate-draft/${enrollmentId}`)
                .then(response => response.data);
        },
        submitQuarterlyReport({ commit }, { enrollmentId, data }) {
            return window.axios.post(`/teacher/quarterly-reports/${enrollmentId}`, data).then(response => {
                commit('REMOVE_PENDING', enrollmentId);
                if (response.data && response.data.data) {
                    commit('ADD_TO_HISTORY', response.data.data);
                }
                return response;
            });
        },
        resubmitQuarterlyReport({ commit }, { reportId, data }) {
            return window.axios.put(`/teacher/quarterly-reports/resubmit/${reportId}`, data).then(response => {
                // Drop the reversed placeholder from the pending list — it's now back to "submitted" status.
                commit('REMOVE_REVERSED_PENDING', reportId);
                if (response.data && response.data.data) {
                    commit('ADD_TO_HISTORY', response.data.data);
                }
                return response;
            });
        },
        loadQuarterlyHistory({ state, commit }, payload = {}) {
            const next = state.historyNext;
            if (next === null && !payload.initialLoad) return Promise.resolve(); // no more pages

            let url;
            if (payload.initialLoad || next === undefined || !next) {
                const params = new URLSearchParams();
                if (payload.batch_id)    params.append('batch_id', payload.batch_id);
                if (payload.cycle_month) params.append('cycle_month', payload.cycle_month);
                const qs = params.toString();
                url = '/teacher/quarterly-reports/history' + (qs ? `?${qs}` : '');
            } else {
                url = next;
            }

            return window.axios.get(url).then(response => {
                const isInitial = payload.initialLoad || next === undefined;
                const links = response.data.links || {};
                const commitName = isInitial ? 'SET_HISTORY' : 'APPEND_HISTORY';
                commit(commitName, { data: response.data.data, next: links.next || null });
                return response;
            });
        },
        resetQuarterlyHistory({ commit }) {
            commit('SET_HISTORY', { data: [], next: undefined });
        },
    },
};
